"use client"

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuthHeader, getToken } from '../utils/auth';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useBranding } from "../hooks/useBranding";
import { isEnglishOperatorMode } from '../utils/operatorMode';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const handleCopy = async (text, setter, successMessage = "コピーしました！") => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setter(successMessage);
    setTimeout(() => setter(""), 1500);
};

// --- Icons ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const CopyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>;
const MailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const RefreshIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21.5 2v6h-6"/><path d="M2.5 22v-6h6"/><path d="M21.5 8a10 10 0 0 0-20-4v2"/><path d="M2.5 16a10 10 0 0 0 20 4v-2"/></svg>;
const StarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const ClipboardIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>;
const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>;
const HelpIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
const ArrowRightIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>;
const CreditCardIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>;

// --- Sub Components ---

// 1. Webトラッキング設定 (Meta広告連携 強化版)
// 1. Webトラッキング設定 (GTM / WordPress / HTML直貼り 統合版)
function TrackingSettings({ clientId, isEnglishBeta = false }) {
    const { appName } = useBranding();
    const [copyMsg, setCopyMsg] = useState("");
    const [defaultToken, setDefaultToken] = useState("");
    const [activeMethod, setActiveMethod] = useState("gtm"); // デフォルトをGTM(推奨)に設定
    const copy = isEnglishBeta ? {
        copied: "Copied!",
        recommended: "Recommended",
        title: "Tracking Tag Setup",
        subtitle: "Choose the best install method for your website.",
        methods: [
            { id: 'gtm', label: 'Google Tag Manager', sub: 'Recommended' },
            { id: 'direct', label: 'Direct HTML', sub: 'Code install' },
            { id: 'wp', label: 'WordPress', sub: 'Theme / plugin' }
        ],
        downloadTemplate: "Download the GTM template (.tpl)",
        gtmStep2: "In Google Tag Manager, open Templates, create a new tag template, import the file, and save it.",
        gtmStep3: `Create a tag with the ${appName} tracking template, paste the client ID below, and fire it on All Pages.`,
        clientIdLabel: "YOUR CLIENT ID:",
        copyId: "Copy ID",
        directHelp: "Paste this code inside the <head> tag of your website. Tracking starts after the script loads.",
        copyTag: "Copy Tag",
        wpThemeTitle: "If you use a theme such as SWELL or Cocoon",
        wpThemeBody: "Paste the direct HTML tag into the theme analytics/custom script field for the head section.",
        wpPluginTitle: "If you use a plugin",
        wpPluginBody: "Install a header-injection plugin such as WP Insert Headers and Footers, then paste the tag into Scripts in Header.",
        showDirect: "View the paste-ready tag",
        specialTitle: "If your form is custom",
        specialBody: "Standard HTML forms are detected automatically. If your site uses custom JavaScript submission or a special button structure, contact support and we will add it to the detection rules."
    } : {
        copied: "コピーしました！",
        recommended: "Recommend",
        title: "計測タグの設置",
        subtitle: "サイトに合わせて最適な設置方法を選択してください",
        methods: [
            { id: 'gtm', label: 'Googleタグマネージャー', sub: '推奨' },
            { id: 'direct', label: 'HTMLに直貼り', sub: 'コード編集' },
            { id: 'wp', label: 'WordPress', sub: 'テーマ・プラグイン' }
        ],
        downloadTemplate: "こちらからGTMテンプレート(.tpl)",
        gtmStep2: "GTM管理画面の「テンプレート」メニューから「タグテンプレート ＞ 新規」を開き、右上のメニューから「インポート」を選択して保存します。",
        gtmStep3: `「タグ」の設定で「${appName} 追跡タグ」を選択し、以下のIDを貼り付けて「All Pages」で配信してください。`,
        clientIdLabel: "YOUR CLIENT ID:",
        copyId: "IDをコピーする",
        directHelp: "以下のコードをWebサイトの <head> 内に貼り付けるだけで、全ての計測が開始されます。",
        copyTag: "タグをコピーする",
        wpThemeTitle: "● テーマ（SWELL / Cocoon等）をお使いの場合",
        wpThemeBody: "テーマ設定の「アクセス解析」や「カスタムスクリプト」の「head内」に、直貼り用のタグをペーストするだけで完了です。",
        wpPluginTitle: "● プラグインを使用する場合",
        wpPluginBody: "「WP Insert Headers and Footers」等のプラグインをインストールし、設定画面の「Scripts in Header」欄にタグを貼り付けてください。",
        showDirect: "→ 貼り付け用のタグを確認する",
        specialTitle: "特殊なフォームをお使いの場合",
        specialBody: "標準的なHTMLフォームであれば自動的に検知されます。もし、独自のJavaScriptで送信処理を行っている、またはボタンの構成が特殊でデータが飛ばない場合は、弊社サポートまでご連絡ください。バックエンド側で即座に検知対象へ追加いたします。"
    };

    // --- デフォルトフォームのトークンを取得する処理 ---
    useEffect(() => {
        const fetchDefaultToken = async () => {
            try {
                const res = await fetch(`${API_URL}/api/marketing/forms?client_id=${clientId}`, { 
                    headers: getAuthHeader() 
                });
                const data = await res.json();
                if (data.success && data.forms.length > 0) {
                    // 「デフォルト」という名前のフォームを探すか、なければ最初の1件を使用
                    const defaultForm = data.forms.find(f => f.title.includes('デフォルト') || String(f.title || '').toLowerCase().includes('default')) || data.forms[0];
                    setDefaultToken(defaultForm.public_token);
                }
            } catch (e) {
                console.error("トークンの取得に失敗しました", e);
            }
        };
        if (clientId) fetchDefaultToken();
    }, [clientId]);

    // HTML直貼り用のワンタグ
    const oneTag = `<script src="${API_URL}/v1/sb-track.js?id=${clientId}${defaultToken ? `&token=${defaultToken}` : ''}" async></script>`;

    // GTM用テンプレートファイルのダウンロード
    // 🚀 修正：静的ファイルではなく、バックエンドの動的生成APIから取得する
    const handleDownloadGtmTemplate = () => {
        // トークンを取得
        const token = getToken();
        // 🚀 API経由でブランド情報が注入された .tpl ファイルをダウンロード
        window.location.href = `${API_URL}/api/oem/download-gtm-template?token=${token}`;
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* --- メイン：計測タグ設置セクション --- */}
            <div className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-3xl shadow-sm border border-indigo-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4">
                    <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
                        {copy.recommended}
                    </span>
                </div>

                <div className="flex items-start sm:items-center gap-3 mb-8 pr-16 sm:pr-0">
                    <div className="bg-indigo-600 p-3 rounded-2xl text-white shadow-lg shadow-indigo-200">
                        <SettingsIcon />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-800">{copy.title}</h3>
                        <p className="text-xs text-gray-400 font-medium">{copy.subtitle}</p>
                    </div>
                </div>

                {/* 設置方法切り替えタブ */}
                <div className="flex flex-wrap gap-2 p-1 bg-gray-100/80 rounded-2xl mb-8 w-full sm:w-fit">
                    {copy.methods.map(m => (
                        <button 
                            key={m.id}
                            onClick={() => setActiveMethod(m.id)}
                            className={`flex-1 sm:flex-none px-3 sm:px-6 py-3 rounded-xl transition-all duration-200 ${
                                activeMethod === m.id 
                                ? 'bg-white text-indigo-600 shadow-md scale-105' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            <div className="text-[10px] font-black uppercase tracking-widest leading-none mb-1 opacity-60">{m.sub}</div>
                            <div className="text-xs sm:text-sm font-black">{m.label}</div>
                        </button>
                    ))}
                </div>

                <div className="space-y-6">
                    {/* --- パターンA: GTM (Google Tag Manager) --- */}
                    {activeMethod === 'gtm' && (
                        <div className="animate-in slide-in-from-left-2 duration-300">
                            <div className="p-4 sm:p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100 mb-6">
                                <ol className="text-sm text-indigo-900 space-y-4 list-decimal pl-5 font-bold">
                                    <li>
                                        <button 
                                            onClick={handleDownloadGtmTemplate}
                                            className="text-indigo-600 underline hover:text-indigo-800 decoration-2 transition-colors"
                                        >
                                            {copy.downloadTemplate}
                                        </button>
                                    </li>
                                    <li>{copy.gtmStep2}</li>
                                    <li>{copy.gtmStep3}</li>
                                </ol>
                            </div>
                            <div className="bg-gray-900 p-4 sm:p-6 rounded-2xl sm:rounded-3xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-2xl border-4 border-gray-800">
                                <div className="min-w-0 text-indigo-300 font-mono text-sm">
                                    <span className="text-gray-500 mr-2">{copy.clientIdLabel}</span>
                                    <span className="text-white text-base sm:text-lg font-bold select-all break-all">{clientId}</span>
                                </div>
                                <button 
                                    onClick={() => handleCopy(clientId, setCopyMsg, copy.copied)} 
                                    className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl text-xs font-black shadow-xl transition-all active:scale-95 whitespace-nowrap"
                                >
                                    {copyMsg || copy.copyId}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* --- パターンB: HTMLに直貼り (既存機能) --- */}
                    {activeMethod === 'direct' && (
                        <div className="animate-in slide-in-from-left-2 duration-300">
                            <div className="p-4 sm:p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100 mb-6">
                                <p className="text-sm text-indigo-900 leading-relaxed font-bold">
                                    {isEnglishBeta ? (
                                        <>
                                            Paste this code inside the <code className="bg-white px-2 py-0.5 rounded border border-indigo-200">&lt;head&gt;</code> tag of your website. Tracking starts after the script loads.
                                        </>
                                    ) : (
                                        <>
                                            以下のコードをWebサイトの <code className="bg-white px-2 py-0.5 rounded border border-indigo-200">&lt;head&gt;</code> 内に貼り付けるだけで、全ての計測が開始されます。
                                        </>
                                    )}
                                </p>
                            </div>
                            <div className="relative group">
                                <textarea 
                                    readOnly 
                                    className="w-full h-32 sm:h-24 p-4 sm:p-6 sm:pr-44 bg-gray-900 text-indigo-300 font-mono text-xs sm:text-sm rounded-2xl sm:rounded-3xl border-4 border-gray-800 focus:ring-0 shadow-2xl"
                                    value={oneTag}
                                ></textarea>
                                <button 
                                    onClick={() => handleCopy(oneTag, setCopyMsg, copy.copied)} 
                                    className="static sm:absolute sm:bottom-4 sm:right-4 mt-3 sm:mt-0 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl text-xs font-black shadow-xl transition-all"
                                >
                                    {copyMsg || copy.copyTag}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* --- パターンC: WordPress --- */}
                    {activeMethod === 'wp' && (
                        <div className="animate-in slide-in-from-left-2 duration-300">
                            <div className="p-4 sm:p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100 space-y-4">
                                <div className="space-y-2">
                                    <p className="text-sm text-indigo-900 font-bold">{copy.wpThemeTitle}</p>
                                    <p className="text-xs text-indigo-700/70 pl-4">{copy.wpThemeBody}</p>
                                </div>
                                <div className="space-y-2 pt-2">
                                    <p className="text-sm text-indigo-900 font-bold">{copy.wpPluginTitle}</p>
                                    <p className="text-xs text-indigo-700/70 pl-4">{copy.wpPluginBody}</p>
                                </div>
                                <div className="mt-4 p-4 bg-white rounded-xl border border-indigo-100 text-center">
                                    <button 
                                        onClick={() => setActiveMethod('direct')}
                                        className="text-xs font-black text-indigo-600 hover:underline"
                                    >
                                        {copy.showDirect}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- サブ：補足説明 --- */}
            <div className="bg-gray-100/50 p-4 sm:p-6 rounded-2xl border border-dashed border-gray-300">
                <h4 className="text-sm font-bold text-gray-500 mb-2 flex items-center gap-2">
                    <HelpIcon className="w-4 h-4" /> {copy.specialTitle}
                </h4>
                <p className="text-[11px] text-gray-400 leading-relaxed font-medium">
                    {copy.specialBody}
                </p>
            </div>
        </div>
    );
}

// 2. NGリスト設定 (メルマガ用)
function NgListSettings({ clientId }) {
    const [ngList, setNgList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);

    // データ取得
    const fetchNgList = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/mail-ng-list?client_id=${clientId}`, { headers: getAuthHeader() });
            const contentType = res.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                console.warn("Received non-JSON response from ng-list API");
                return;
            }
            const data = await res.json();
            if (data.success) {
                setNgList(data.list);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchNgList(); }, [fetchNgList]);

    // 削除
    const handleDelete = async (id) => {
        if (!window.confirm("このNG設定を解除しますか？")) return;
        try {
            const res = await fetch(`${API_URL}/api/marketing/mail-ng-list/${id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            if (res.ok) fetchNgList();
        } catch (e) { alert("通信エラー"); }
    };

    // 追加モーダル保存
    const handleSave = async (data) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/mail-ng-list`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ ...data, client_id: clientId })
            });
            const result = await res.json();
            if (result.success) {
                setShowModal(false);
                fetchNgList();
            } else {
                alert("エラー: " + result.error);
            }
        } catch (e) { alert(e.message); }
    };

    const filteredList = ngList.filter(item => 
        (item.email && item.email.includes(searchTerm)) || 
        (item.domain && item.domain.includes(searchTerm)) || 
        (item.reason && item.reason.includes(searchTerm))
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="relative w-full sm:flex-grow sm:max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"><SearchIcon /></div>
                    <input 
                        type="text" 
                        className="w-full pl-10 p-2 border rounded text-sm" 
                        placeholder="検索..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <button onClick={() => setShowModal(true)} className="flex w-full sm:w-auto items-center justify-center gap-1 bg-red-600 text-white font-bold py-2 px-4 rounded hover:bg-red-700 text-sm">
                    <PlusIcon /> 追加
                </button>
            </div>

            <div className="hidden md:block bg-white rounded-lg shadow border overflow-hidden">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="bg-gray-100 text-gray-700 uppercase">
                        <tr>
                            <th className="p-3">種別</th>
                            <th className="p-3">対象</th>
                            <th className="p-3">理由</th>
                            <th className="p-3">登録日</th>
                            <th className="p-3 text-center">操作</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {isLoading ? <tr><td colSpan="5" className="p-8 text-center">読み込み中...</td></tr> :
                         filteredList.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-gray-500">データがありません</td></tr> :
                         filteredList.map(item => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                <td className="p-3">
                                    {item.email ? <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">Email</span> : <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded text-xs">Domain</span>}
                                </td>
                                <td className="p-3 font-mono font-bold text-gray-800">{item.email || item.domain}</td>
                                <td className="p-3">{item.reason}</td>
                                <td className="p-3 text-xs">{new Date(item.created_at).toLocaleDateString()}</td>
                                <td className="p-3 text-center">
                                    <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><TrashIcon /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="md:hidden space-y-3">
                {isLoading ? (
                    <div className="bg-white rounded-lg border p-6 text-center text-sm text-gray-500">読み込み中...</div>
                ) : filteredList.length === 0 ? (
                    <div className="bg-white rounded-lg border p-6 text-center text-sm text-gray-500">データがありません</div>
                ) : filteredList.map(item => (
                    <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <span className={`shrink-0 px-2 py-0.5 rounded text-xs ${item.email ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                                {item.email ? 'Email' : 'Domain'}
                            </span>
                            <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><TrashIcon /></button>
                        </div>
                        <div className="font-mono font-bold text-gray-800 break-all">{item.email || item.domain}</div>
                        {item.reason && <div className="text-sm text-gray-600 break-words">{item.reason}</div>}
                        <div className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString()}</div>
                    </div>
                ))}
            </div>
            {showModal && <AddNgModal onClose={() => setShowModal(false)} onSave={handleSave} />}
        </div>
    );
}

function AddNgModal({ onClose, onSave }) {
    const [email, setEmail] = useState("");
    const [domain, setDomain] = useState("");
    const [reason, setReason] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ email, domain, reason });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="font-bold text-lg mb-4">NGリストに追加</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold mb-1">メールアドレス (完全一致)</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border p-2 rounded" placeholder="user@example.com" />
                    </div>
                    <div className="text-center text-gray-400 text-xs">- または -</div>
                    <div>
                        <label className="block text-sm font-bold mb-1">ドメイン (後方一致)</label>
                        <input type="text" value={domain} onChange={e => setDomain(e.target.value)} className="w-full border p-2 rounded" placeholder="example.com" />
                        <p className="text-xs text-red-500 mt-1">※Gmail等のフリーメールは指定しないでください。</p>
                    </div>
                    <div>
                        <label className="block text-sm font-bold mb-1">理由</label>
                        <input type="text" value={reason} onChange={e => setReason(e.target.value)} className="w-full border p-2 rounded" placeholder="配信停止希望など" required />
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
                        <button type="button" onClick={onClose} className="w-full sm:w-auto px-4 py-2 border rounded hover:bg-gray-50">キャンセル</button>
                        <button type="submit" disabled={!email && !domain} className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white font-bold rounded hover:bg-red-700 disabled:bg-gray-400">追加</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const DEFAULT_DOMAIN_SETTINGS = {
    sender: {
        fallback_sender: 'mailer@sbroute.net',
        verified_sender: null,
    },
    tracking: {
        current_domain: 'track.sbroute.net',
        current_base_url: 'https://track.sbroute.net',
        fallback_domain: 'track.sbroute.net',
        fallback_base_url: 'https://track.sbroute.net',
        source: 'system',
        status: 'active',
        covers: ['click', 'open', 'unsubscribe'],
        dns_status: null,
        ssl_status: null,
        route_status: null,
        warnings: [],
    },
    custom_tracking_domains: [],
    app: {
        current_domain: 'salesbreaker.jp',
        source: 'system',
    },
};

function DomainSettingsOverview({ clientId }) {
    const [domainSettings, setDomainSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchDomainSettings = useCallback(async () => {
        if (!clientId) {
            setDomainSettings(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/api/domain-settings/current?client_id=${clientId}`, { headers: getAuthHeader() });
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('DNS設定の取得に失敗しました。');
            }

            const data = await res.json();
            if (!data.success) {
                throw new Error(data.error || 'DNS設定の取得に失敗しました。');
            }
            setDomainSettings(data);
        } catch (err) {
            console.error('Domain settings fetch error:', err);
            setDomainSettings(null);
            setError(err.message || 'DNS設定の取得に失敗しました。共有基盤の情報を表示しています。');
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchDomainSettings(); }, [fetchDomainSettings]);

    const effectiveSettings = domainSettings || DEFAULT_DOMAIN_SETTINGS;
    const senderSettings = effectiveSettings.sender || DEFAULT_DOMAIN_SETTINGS.sender;
    const trackingSettings = effectiveSettings.tracking || DEFAULT_DOMAIN_SETTINGS.tracking;

    const fallbackSender = senderSettings.fallback_sender || senderSettings.fallback || 'mailer@sbroute.net';
    const verifiedSender = senderSettings.verified_sender || null;
    const trackingDomain = trackingSettings.current_domain || 'track.sbroute.net';
    const trackingSource = trackingSettings.source || 'system';
    const fallbackTrackingDomain = trackingSettings.fallback_domain || 'track.sbroute.net';
    const senderStatus = verifiedSender ? '設定済み' : '未設定';
    const trackingStatus = trackingSource === 'client_custom' ? '設定済み' : '標準設定';
    const trackingRecordText = trackingSource === 'client_custom'
        ? `${trackingDomain} CNAME ${fallbackTrackingDomain}`
        : `track.example.com CNAME ${fallbackTrackingDomain}`;

    const DomainCard = ({ title, description, children }) => (
        <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
            <div>
                <h3 className="text-base sm:text-lg font-black text-gray-900">{title}</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );

    const InfoRow = ({ label, value, note, tone = 'gray' }) => {
        const toneClass = {
            gray: 'bg-gray-50 text-gray-900 border-gray-100',
            green: 'bg-green-50 text-green-800 border-green-100',
            blue: 'bg-blue-50 text-blue-800 border-blue-100',
            amber: 'bg-amber-50 text-amber-800 border-amber-100',
        }[tone] || 'bg-gray-50 text-gray-900 border-gray-100';

        return (
            <div className={`rounded-xl border p-4 ${toneClass}`}>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</p>
                <p className="mt-1 font-mono text-sm sm:text-base font-black break-all">{value}</p>
                {note && <p className="mt-2 text-xs leading-relaxed opacity-80">{note}</p>}
            </div>
        );
    };

    return (
        <div className="space-y-5 sm:space-y-6">
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-7">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-black text-gray-900 mt-2">DNS設定</h2>
                        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-3xl">
                            必要なDNS設定をまとめて確認するページです。
                            未設定の場合はSalesBreaker管理ドメインを使用します。
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={fetchDomainSettings}
                        disabled={isLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-xs font-black text-gray-600 hover:bg-gray-100 disabled:opacity-60"
                    >
                        <RefreshIcon className={isLoading ? 'animate-spin' : ''} />
                        再確認
                    </button>
                </div>
                {error && (
                    <div className="mt-4 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-bold text-red-700">
                        {error}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <DomainCard
                    title="現在の設定"
                    description="いま利用しているDNS関連の設定です。"
                >
                    <InfoRow
                        label="送信用DNS"
                        value={isLoading ? '確認中...' : senderStatus}
                        note={verifiedSender ? `認証済み: ${verifiedSender}` : `未設定時はSalesBreaker管理アドレスを使用します（${fallbackSender}）。`}
                        tone={verifiedSender ? 'green' : 'gray'}
                    />
                    <InfoRow
                        label="計測用DNS"
                        value={isLoading ? '確認中...' : trackingStatus}
                        note={trackingSource === 'client_custom' ? `${trackingDomain} を使用中です。` : `${fallbackTrackingDomain} を使用中です。`}
                        tone="green"
                    />
                    <InfoRow
                        label="補足"
                        value="追加設定なしでも利用できます"
                        note="自社ドメインで使う場合だけ、右側のDNSを追加します。"
                        tone="blue"
                    />
                </DomainCard>

                <DomainCard
                    title="DNSに追加する内容"
                    description="自社ドメインで利用する場合は、表示された内容をDNSに追加してください。"
                >
                    <InfoRow
                        label="送信用DNS"
                        value={verifiedSender ? '設定済み' : '送信元設定で表示されるDNSを追加'}
                        note={verifiedSender ? '送信用DNSは確認済みです。' : '送信元設定に表示されるDKIMとクリック計測用CNAMEを追加してください。'}
                        tone={verifiedSender ? 'green' : 'amber'}
                    />
                    <InfoRow
                        label="計測用DNS"
                        value={trackingRecordText}
                        note={trackingSource === 'client_custom' ? '計測用DNSは確認済みです。' : '自社ドメイン化する場合の例です。設定しない場合は標準ドメインを使用します。'}
                        tone={trackingSource === 'client_custom' ? 'green' : 'gray'}
                    />
                    <InfoRow
                        label="確認"
                        value={trackingSource === 'client_custom' ? '設定済み' : '必要な場合のみ設定'}
                        note={trackingSource === 'client_custom' ? 'DNS / HTTPS / 接続確認済みです。' : '通常利用では追加作業は不要です。'}
                        tone="blue"
                    />
                </DomainCard>
            </div>
        </div>
    );
}

// 3. 送信元設定 (SES)
function SenderSettings({ clientId, isEnglishBeta = false }) {
    const [profiles, setProfiles] = useState([]);
    const [editingProfile, setEditingProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState({});
    const [message, setMessage] = useState(null);
    const copy = isEnglishBeta ? {
        introTitle: "Sender Email & DNS Setup",
        introBody: "Add sender addresses for marketing email. Sending requires DNS verification through DKIM records. The same setup also shows the tracking CNAME used for click, open, and unsubscribe links.",
        invalidResponse: (status) => `Server error: invalid response returned. (Status: ${status})`,
        saveFailed: "Failed to save.",
        deleteConfirm: "Delete this sender profile?",
        deleteFailed: "Failed to delete.",
        senderRequired: "Please enter a sender email address.",
        verifyStarting: "Starting verification...",
        apiInvalid: (status) => `API error: invalid response returned by the server. The backend may need to be reloaded. (Status: ${status})`,
        verifySuccess: "Verification completed. This domain can now send email.",
        verifyPending: "Verification is pending. Please check the DNS records.",
        verifiedRecords: "Verified DNS Records",
        pendingRecords: "DNS Records Required",
        verifiedRecordHelp: "These are the DNS records used by this sender. Use them to confirm what is configured in your DNS provider.",
        pendingRecordHelp: (count) => `Log in to your DNS provider and add the ${count} DNS records below.`,
        dkimTrackHelp: "The 3 DKIM records verify the sender domain. The track CNAME customizes click, open, and unsubscribe URLs.",
        verifiedNote: "Verification is complete. If you changed DNS, use the button below to recheck the latest status.",
        pendingNote: "DNS propagation can take a few minutes and up to 72 hours. After adding records, click the recheck button.",
        recheckVerified: "Recheck Latest Status",
        recheckPending: "I Added DNS. Recheck",
        record: "Record",
        hostLabel: "Paste into Host / Name / Subdomain",
        valueLabel: "Paste into Value / Content / Target",
        copyHost: "Host",
        copyValue: "Value",
        copied: (label) => `${label} copied.`,
        copyFailed: "Copy failed. Please select and copy manually.",
        clickTracking: "Click Tracking",
        trackNote: "This CNAME customizes click tracking, open tracking, and unsubscribe URLs inside emails.",
        editTitle: "Edit & Verify Sender Profile",
        newTitle: "Add Sender Profile",
        basicInfo: "Basic Information",
        profileName: "Profile name",
        profilePlaceholder: "Head Office",
        useDefault: "Use as default",
        companyName: "Company / Trade Name",
        companyPlaceholder: "Company Inc.",
        representativeName: "Representative (optional)",
        representativePlaceholder: "Jane Smith",
        address: "Address",
        postalPlaceholder: "100-0000",
        addressPlaceholder: "Street, city, state...",
        phone: "Phone",
        phonePlaceholder: "+1 555 123 4567",
        contactEmail: "Contact Email",
        senderSettings: "Email Sending Settings (AWS SES)",
        fromAddress: "From Address",
        senderHelp: "A sender address on your own domain is recommended.",
        currentStatus: "Current status:",
        refreshStatus: "Check Latest Status",
        verificationSteps: "Verification steps:",
        step1: "Click Start Domain Verification.",
        step2: "Copy the CNAME records shown below.",
        step3: "Add them in your DNS provider.",
        step4: "Click Check Latest Status and wait until verification is complete.",
        startVerification: "Start Domain Verification",
        cancel: "Cancel",
        save: "Save",
        addNew: "Add New",
        loading: "Loading...",
        empty: "No sender profiles yet. Add a new one.",
        defaultBadge: "Default",
        needsDns: "DNS Setup",
        viewDns: "View DNS",
        startVerifyShort: "Start Verification",
        edit: "Edit",
        delete: "Delete",
        messageNeedsDns: "DNS setup is required. Review the records below.",
        messageVerifiedDns: "Showing verified DNS records.",
        status: {
            success: "Verified (Success)",
            pending: "Pending",
            failed: "Failed",
            checking: "Checking...",
            starting: "Starting...",
            error: "Error",
            notVerified: "Not Verified"
        }
    } : {
        introTitle: "送信元メールアドレス設定 (AWS SES連携)",
        introBody: "ここに追加されたアドレスは、MAやメルマガの送信元として利用できます。送信するにはDNS設定によるドメイン検証(Verify)が必要です。",
        invalidResponse: (status) => `サーバーエラー: 無効な応答が返されました (Status: ${status})`,
        saveFailed: "保存に失敗しました",
        deleteConfirm: "この送信元プロファイルを削除しますか？",
        deleteFailed: "削除に失敗しました",
        senderRequired: "送信元メールアドレスを入力してください。",
        verifyStarting: "検証プロセスを開始中...",
        apiInvalid: (status) => `APIエラー: サーバーから無効な応答(HTML等)が返されました。バックエンド(server.js)が再起動されていない可能性があります。(Status: ${status})`,
        verifySuccess: "検証が完了しました！このドメインでメール送信が可能です。",
        verifyPending: "検証は保留中です。DNSレコードの設定を確認してください。",
        verifiedRecords: "✅ 設定済みDNSレコード",
        pendingRecords: "⚠️ DNS管理画面での設定が必要です",
        verifiedRecordHelp: "この送信元で利用しているDNSレコードです。ドメイン管理画面に入れた内容の確認用として利用できます。",
        pendingRecordHelp: (count) => `ドメイン管理画面（お名前.com、ムームードメイン等）にログインし、以下の ${count}つのDNSレコード を追加してください。`,
        dkimTrackHelp: "DKIM 3件はメール送信元の認証、track 1件はクリック計測URL用です。",
        verifiedNote: "※ 検証は完了しています。DNSを変更した場合は、下のボタンで最新状態を再確認できます。",
        pendingNote: "※ 反映（浸透）まで数分〜最大72時間かかる場合があります。設定後は以下のボタンを押して確認してください。",
        recheckVerified: "最新の状態を再確認する",
        recheckPending: "設定が完了したので再確認する",
        record: "レコード",
        hostLabel: "▼ ホスト名 / サブドメイン 欄に貼り付け",
        valueLabel: "▼ 値 / 内容 / 転送先 欄に貼り付け",
        copyHost: "ホスト名",
        copyValue: "値",
        copied: (label) => `${label}をコピーしました`,
        copyFailed: "コピーに失敗しました。手動で選択してコピーしてください。",
        clickTracking: "クリック計測",
        trackNote: "メール内のクリック計測URLや配信停止URLを自社ドメイン化するための設定です。",
        editTitle: "送信元プロフィールの編集・検証",
        newTitle: "新規送信元の追加",
        basicInfo: "基本情報 (特定電子メール法に基づく表記)",
        profileName: "管理名 (例: 本社)",
        profilePlaceholder: "",
        useDefault: "デフォルトとして使用",
        companyName: "会社名 / 屋号",
        companyPlaceholder: "株式会社〇〇",
        representativeName: "代表者名 (任意)",
        representativePlaceholder: "山田 太郎",
        address: "住所 (特定電子メール法で必須)",
        postalPlaceholder: "〒100-0000",
        addressPlaceholder: "東京都千代田区...",
        phone: "電話番号",
        phonePlaceholder: "03-1234-5678",
        contactEmail: "連絡先メールアドレス",
        senderSettings: "メール送信設定 (AWS SES)",
        fromAddress: "Fromアドレス (送信元)",
        senderHelp: "※ 独自ドメインのメールアドレスを推奨します。",
        currentStatus: "現在のステータス:",
        refreshStatus: "最新の状態を確認",
        verificationSteps: "検証手順:",
        step1: "「ドメイン検証開始」ボタンを押します。",
        step2: "表示されるCNAMEレコードをコピーします。",
        step3: "お使いのドメイン管理画面（お名前.com等）でDNSレコードを追加します。",
        step4: "「最新の状態を確認」ボタンを押し、「検証完了」になるのを待ちます。",
        startVerification: "ドメイン検証を開始する (推奨)",
        cancel: "キャンセル",
        save: "保存する",
        addNew: "新規追加",
        loading: "読み込み中...",
        empty: "送信元プロファイルがありません。「新規追加」してください。",
        defaultBadge: "デフォルト",
        needsDns: "DNS設定を確認",
        viewDns: "DNS設定を確認",
        startVerifyShort: "検証を開始",
        edit: "編集",
        delete: "削除",
        messageNeedsDns: "DNS設定が必要です。詳細を確認してください。",
        messageVerifiedDns: "検証済みのDNSレコードを表示しています。",
        status: {
            success: "検証完了 (Success)",
            pending: "検証保留中 (Pending)",
            failed: "検証失敗 (Failed)",
            checking: "確認中...",
            starting: "開始中...",
            error: "エラー",
            notVerified: "未設定 (NotVerified)"
        }
    };

    const fetchProfiles = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/issuer-profiles?client_id=${clientId}`, { headers: getAuthHeader() });
            
            const contentType = res.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                console.error("Non-JSON response from fetchProfiles");
                return;
            }

            const data = await res.json();
            if (data.success) {
                const updatedProfiles = data.profiles.filter(p => p.sender_email) || [];
                setProfiles(updatedProfiles);
                
                // ★ 重要: 現在編集中のプロフィールがあれば、取得した最新データで更新する
                // これにより、画面上の Pending ステータスや DNS トークンが即座に反映されます
                setEditingProfile(current => {
                    if (current && current.id) {
                        const latest = updatedProfiles.find(p => p.id === current.id);
                        return latest ? { ...latest } : current;
                    }
                    return current;
                });
                
                // setMessage(null); // ← この行を削除またはコメントアウト
            }
        } catch (err) { console.error(err); }
        finally { setIsLoading(false); }
    }, [clientId]);

    useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

    const handleSave = async (e) => {
        e.preventDefault();
        const method = editingProfile.id ? "PUT" : "POST";
        const url = editingProfile.id 
            ? `${API_URL}/api/issuer-profiles/${editingProfile.id}`
            : `${API_URL}/api/issuer-profiles`;
        
        try {
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", ...getAuthHeader() }, // 修正: trueを削除
                body: JSON.stringify({ ...editingProfile, client_id: clientId })
            });

            const contentType = res.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error(copy.invalidResponse(res.status));
            }

            const data = await res.json(); 
            if (res.ok && data.success) {
                setEditingProfile(null);
                fetchProfiles();
            } else {
                throw new Error(data.error || copy.saveFailed);
            }
        } catch (err) { alert(err.message); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(copy.deleteConfirm)) return;
        try {
            const res = await fetch(`${API_URL}/api/issuer-profiles/${id}`, { 
                method: "DELETE", 
                headers: { "Content-Type": "application/json", ...getAuthHeader() }, 
                body: JSON.stringify({ client_id: clientId })
            });
            if (!res.ok) throw new Error(copy.deleteFailed);
            fetchProfiles();
        } catch (err) { alert(err.message); }
    };

    const handleVerify = async (profileId, email, isDomain) => {
        if (!email) return alert(copy.senderRequired);
        
        setMessage({ type: 'info', text: copy.verifyStarting });
        setVerificationStatus(prev => ({ ...prev, [profileId]: 'Starting...' }));

        try {
            const res = await fetch(`${API_URL}/api/ses/verify-sender`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ 
                    profile_id: profileId, 
                    sender_email: email, 
                    is_domain_verification: isDomain,
                    client_id: clientId // ★ 追加: 管理者代理操作のために現在のクライアントIDを送信
                })
            });

            // コンテンツタイプの確認 (HTMLエラー対策)
            const contentType = res.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await res.text();
                console.error("API Error (Non-JSON):", text);
                throw new Error(copy.apiInvalid(res.status));
            }

            const data = await res.json();

            if (data.success) {
                setMessage({ type: 'success', text: data.message });
                // 即座にステータスチェックを行い、DNSレコードを表示させる
                setTimeout(() => checkStatus(profileId), 1000); 
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            setMessage({ type: 'error', text: `${err.message}` });
            setVerificationStatus(prev => ({ ...prev, [profileId]: 'Failed' }));
        }
    };

    const checkStatus = async (profileId) => {
        setVerificationStatus(prev => ({ ...prev, [profileId]: 'Checking...' }));
        try {
            const res = await fetch(`${API_URL}/api/ses/check-status?profile_id=${profileId}&client_id=${clientId}`, { headers: getAuthHeader() });
            const data = await res.json();

            if (data.success) {
                const newStatus = data.status; // ここに 'Success' が入ってくる
                setVerificationStatus(prev => ({ ...prev, [profileId]: newStatus }));
                
                if (editingProfile && editingProfile.id === profileId) {
                    setEditingProfile({
                        ...editingProfile,
                        ses_verification_status: newStatus,
                        ses_dkim_tokens: data.dkim_tokens
                    });
                }

                // ★ 修正：'Verified' ではなく 'Success' をチェックするように変更
                if (newStatus === 'Success') {
                    setMessage({ type: 'success', text: copy.verifySuccess });
                } else if (newStatus === 'Pending') {
                    setMessage({ type: 'warning', text: copy.verifyPending });
                }
                
                fetchProfiles(); 

            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            setMessage({ type: 'error', text: `${err.message}` });
            setVerificationStatus(prev => ({ ...prev, [profileId]: 'Error' }));
        }
    };

    const isSesVerified = (status) => ['Success', 'Verified'].includes(status);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Success': // ★ 追加
            case 'Verified': return 'bg-green-100 text-green-800 border-green-300';
            case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'Failed': return 'bg-red-100 text-red-800 border-red-300';
            default: return 'bg-gray-100 text-gray-600 border-gray-300';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'Success': // ★ 追加
            case 'Verified': return copy.status.success;
            case 'Pending': return copy.status.pending;
            case 'Failed': return copy.status.failed;
            case 'Checking...': return copy.status.checking;
            case 'Starting...': return copy.status.starting;
            case 'Error': return copy.status.error;
            default: return copy.status.notVerified;
        }
    };

    const copyDnsValue = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value);
            alert(copy.copied(label));
        } catch (err) {
            alert(copy.copyFailed);
        }
    };

    const DnsRecordCard = ({ number, title, type, host, value, note }) => (
        <div className="bg-white p-4 rounded border border-gray-300 shadow-sm space-y-3">
            <div className="flex flex-wrap justify-between items-center gap-2 border-b pb-1">
                <span className="font-bold text-gray-700">{copy.record} {number} ({type})</span>
                <span className="text-[11px] font-bold text-gray-500">{title}</span>
            </div>

            <div>
                <label className="block text-[10px] font-bold text-blue-600 mb-1">
                    {copy.hostLabel}
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        readOnly
                        value={host}
                        className="min-w-0 flex-grow bg-gray-50 border p-1.5 rounded font-mono text-xs text-gray-600"
                    />
                    <button
                        type="button"
                        onClick={() => copyDnsValue(host, copy.copyHost)}
                        className="w-full sm:w-auto px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs"
                    >
                        {isEnglishBeta ? 'Copy' : 'コピー'}
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-[10px] font-bold text-blue-600 mb-1">
                    {copy.valueLabel}
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        readOnly
                        value={value}
                        className="min-w-0 flex-grow bg-gray-50 border p-1.5 rounded font-mono text-xs text-gray-600"
                    />
                    <button
                        type="button"
                        onClick={() => copyDnsValue(value, copy.copyValue)}
                        className="w-full sm:w-auto px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs"
                    >
                        {isEnglishBeta ? 'Copy' : 'コピー'}
                    </button>
                </div>
            </div>

            {note && <p className="text-[11px] text-gray-500 leading-relaxed">{note}</p>}
        </div>
    );

    const getDnsRecords = (profile) => {
        if (!profile || !profile.sender_email) return null;
        
        let tokens = profile.ses_dkim_tokens;
        if (typeof tokens === 'string') {
            try { tokens = JSON.parse(tokens); } catch (e) { tokens = null; }
        }

        if (Array.isArray(tokens) && tokens.length > 0) {
            const verified = isSesVerified(profile.ses_verification_status);
            const additionalRecords = [
                {
                    title: copy.clickTracking,
                    type: 'CNAME',
                    host: 'track',
                    value: 'track.sbroute.net',
                    note: copy.trackNote
                }
            ];
            const recordCount = tokens.length + additionalRecords.length;

            return (
                <div className={`mt-4 p-4 border-2 rounded text-sm space-y-4 ${verified ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-400'}`}>
                    <div>
                        <p className={`font-bold text-lg flex items-center gap-2 ${verified ? 'text-green-900' : 'text-yellow-900'}`}>
                            {verified ? copy.verifiedRecords : copy.pendingRecords}
                        </p>
                        <p className={`${verified ? 'text-green-800' : 'text-yellow-800'} mt-2 leading-relaxed`}>
                            {verified ? (
                                <>
                                    {copy.verifiedRecordHelp}
                                </>
                            ) : (
                                <>
                                    {copy.pendingRecordHelp(recordCount)}
                                </>
                            )}
                        </p>
                        <p className={`text-xs ${verified ? 'text-green-700' : 'text-yellow-700'} mt-2`}>
                            {copy.dkimTrackHelp}
                        </p>
                    </div>

                    <div className="space-y-4">
                        {tokens.map((token, index) => (
                            <DnsRecordCard
                                key={token}
                                number={index + 1}
                                title="DKIM"
                                type="CNAME"
                                host={`${token}._domainkey`}
                                value={`${token}.dkim.amazonses.com`}
                            />
                        ))}
                        {additionalRecords.map((record, index) => (
                            <DnsRecordCard
                                key={record.title}
                                number={tokens.length + index + 1}
                                title={record.title}
                                type={record.type}
                                host={record.host}
                                value={record.value}
                                note={record.note}
                            />
                        ))}
                    </div>

                    <div className={`bg-white p-3 rounded border text-xs text-gray-600 ${verified ? 'border-green-200' : 'border-yellow-200'}`}>
                        <p>
                            {verified
                                ? copy.verifiedNote
                                : copy.pendingNote}
                        </p>
                    </div>

                    <div className="text-right">
                        <button 
                            type="button" 
                            onClick={() => checkStatus(profile.id)} 
                            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-blue-700 shadow"
                        >
                            <RefreshIcon className="inline w-4 h-4 mr-1"/> {verified ? copy.recheckVerified : copy.recheckPending}
                        </button>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded border border-blue-100 text-sm text-blue-800 mb-4">
                <p><strong>{copy.introTitle}</strong></p>
                <p className="text-xs mt-1">{copy.introBody}</p>
            </div>

            {/* メッセージ表示エリア */}
            {message && (
                <div className={`p-4 rounded-md mb-4 text-sm font-bold flex items-center gap-2 ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : message.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-yellow-100 text-yellow-800 border border-yellow-200'}`}>
                    {message.type === 'warning' && <span className="text-xl">⚠️</span>}
                    {message.text}
                </div>
            )}

            {/* 編集・新規追加フォーム */}
            {editingProfile ? (
                <div className="border p-4 sm:p-6 rounded-lg bg-gray-50 shadow-inner">
                    <h4 className="font-bold text-gray-800 mb-4 text-lg border-b pb-2">
                        {editingProfile.id ? copy.editTitle : copy.newTitle}
                    </h4>
                    <form onSubmit={handleSave} className="space-y-4">
                        {/* プロフィール基本情報 */}
                        <div className="p-4 border rounded bg-white">
                            <h5 className="font-bold text-gray-700 mb-3 text-sm">{copy.basicInfo}</h5>
                            
                            {/* ★ 1行目: 管理名 & デフォルト設定 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 block mb-1">{copy.profileName}</label>
                                    <input type="text" required className="w-full border p-2 rounded text-sm" placeholder={copy.profilePlaceholder} value={editingProfile.profile_name || ''} onChange={e => setEditingProfile({...editingProfile, profile_name: e.target.value})} />
                                </div>
                                <div className="flex items-center md:pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={editingProfile.is_default} onChange={e => setEditingProfile({...editingProfile, is_default: e.target.checked})} className="w-4 h-4 text-blue-600"/>
                                        <span className="text-sm">{copy.useDefault}</span>
                                    </label>
                                </div>
                            </div>

                            {/* ★ 2行目: 会社名 & 代表者名 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 block mb-1">{copy.companyName} <span className="text-red-500">*</span></label>
                                    <input type="text" required className="w-full border p-2 rounded text-sm" placeholder={copy.companyPlaceholder} value={editingProfile.company_name || ''} onChange={e => setEditingProfile({...editingProfile, company_name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 block mb-1">{copy.representativeName}</label>
                                    <input type="text" className="w-full border p-2 rounded text-sm" placeholder={copy.representativePlaceholder} value={editingProfile.representative_name || ''} onChange={e => setEditingProfile({...editingProfile, representative_name: e.target.value})} />
                                </div>
                            </div>

                            {/* ★ 3行目: 住所 (追加) */}
                            <div className="mb-3">
                                <label className="text-xs font-bold text-gray-500 block mb-1">{copy.address} <span className="text-red-500">*</span></label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input type="text" className="w-full sm:w-32 border p-2 rounded text-sm" placeholder={copy.postalPlaceholder} value={editingProfile.postal_code || ''} onChange={e => setEditingProfile({...editingProfile, postal_code: e.target.value})} />
                                    <input type="text" required className="flex-grow border p-2 rounded text-sm" placeholder={copy.addressPlaceholder} value={editingProfile.address || ''} onChange={e => setEditingProfile({...editingProfile, address: e.target.value})} />
                                </div>
                            </div>

                            {/* ★ 4行目: 電話番号 & 連絡用Email (追加) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 block mb-1">{copy.phone}</label>
                                    <input type="tel" className="w-full border p-2 rounded text-sm" placeholder={copy.phonePlaceholder} value={editingProfile.phone || ''} onChange={e => setEditingProfile({...editingProfile, phone: e.target.value})} />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 block mb-1">{copy.contactEmail}</label>
                                    <input type="email" className="w-full border p-2 rounded text-sm" placeholder="info@example.com" value={editingProfile.email || ''} onChange={e => setEditingProfile({...editingProfile, email: e.target.value})} />
                                </div>
                            </div>
                        </div>

                        {/* MA送信元メール設定 */}
                        <div className="p-4 border rounded bg-indigo-50">
                            <h5 className="font-bold text-indigo-800 mb-3 flex items-center gap-1 text-sm"><MailIcon/> {copy.senderSettings}</h5>
                            
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-bold text-gray-600 block mb-1">{copy.fromAddress} <span className="text-red-500">*</span></label>
                                    <input type="email" required className="w-full border p-2 rounded text-sm font-mono" placeholder="sender@your-domain.com" value={editingProfile.sender_email || ''} onChange={e => setEditingProfile({...editingProfile, sender_email: e.target.value})} />
                                    <p className="text-xs text-gray-500 mt-1">{copy.senderHelp}</p>
                                </div>
                            </div>

                            {/* 検証アクションエリア */}
                            {editingProfile.id && (
                                <div className="mt-4 p-4 bg-white rounded border border-indigo-100">
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(editingProfile.ses_verification_status || 'NotVerified')}`}>
                                            {copy.currentStatus} {getStatusLabel(editingProfile.ses_verification_status || 'NotVerified')}
                                        </span>
                                        <button 
                                            type="button" 
                                            onClick={() => checkStatus(editingProfile.id)} 
                                            disabled={verificationStatus[editingProfile.id] === 'Checking...'}
                                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                        >
                                            <RefreshIcon className={verificationStatus[editingProfile.id] === 'Checking...' ? 'animate-spin' : ''}/> {copy.refreshStatus}
                                        </button>
                                    </div>

                                    {!isSesVerified(editingProfile.ses_verification_status) && (
                                        <div className="space-y-3">
                                            <div className="p-3 bg-gray-100 rounded text-xs text-gray-700">
                                                <p className="font-bold mb-1">{copy.verificationSteps}</p>
                                                <ol className="list-decimal pl-4 space-y-1">
                                                    <li>{copy.step1}</li>
                                                    <li>{copy.step2}</li>
                                                    <li>{copy.step3}</li>
                                                    <li>{copy.step4}</li>
                                                </ol>
                                            </div>
                                            <div className="flex gap-3">
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleVerify(editingProfile.id, editingProfile.sender_email, true)} 
                                                    className="flex-grow flex items-center justify-center gap-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-indigo-700 shadow-sm transition-all"
                                                >
                                                    <MailIcon/> {copy.startVerification}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* DNSレコード表示 (Pending時のみ) */}
                                    {getDnsRecords(editingProfile)}
                                </div>
                            )}
                        </div>
                        
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
                            <button type="button" onClick={() => { setEditingProfile(null); setMessage(null); }} className="w-full sm:w-auto px-5 py-2 border rounded text-gray-600 hover:bg-gray-100">{copy.cancel}</button>
                            <button type="submit" className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow-sm">{copy.save}</button>
                        </div>
                    </form>
                </div>
            ) : (
                // リスト表示モード
                <div>
                    <div className="flex justify-stretch sm:justify-end mb-4">
                        <button onClick={() => { setEditingProfile({ is_default: false }); setMessage(null); }} className="flex w-full sm:w-auto items-center justify-center gap-1 text-sm bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 shadow-sm font-bold">
                            <PlusIcon /> {copy.addNew}
                        </button>
                    </div>
                    {isLoading ? <div className="p-8 text-center text-gray-500"><RefreshIcon className="animate-spin inline mr-2"/>{copy.loading}</div> : (
                        <div className="space-y-3">
                            {profiles.length === 0 && <p className="text-center text-gray-500 py-8 bg-gray-50 rounded border border-dashed">{copy.empty}</p>}
                            
                            {profiles.map(p => (
                                <div key={p.id} className="border p-4 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                    <div className="space-y-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-bold text-lg text-gray-800 break-words">{p.profile_name}</span>
                                            {p.is_default === 1 && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded border border-green-200">{copy.defaultBadge}</span>}
                                        </div>
                                        <p className="text-sm font-mono text-indigo-700 flex items-center gap-1 break-all">
                                            <MailIcon className="w-4 h-4"/> {p.sender_email}
                                        </p>
                                        <p className="text-xs text-gray-500">{p.company_name}</p>
                                    </div>

                                    <div className="flex flex-col items-end gap-2 w-full md:w-auto">
                                        {/* ステータス表示 */}
                                        <div className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${getStatusColor(p.ses_verification_status || 'NotVerified')}`}>
                                            {p.ses_verification_status === 'Pending' && <span className="animate-pulse">●</span>}
                                            {getStatusLabel(p.ses_verification_status || 'NotVerified')}
                                        </div>

                                        <div className="flex flex-wrap items-center justify-end gap-2">
                                            {p.ses_verification_status === 'Pending' ? (
                                                <button type="button" onClick={() => { setEditingProfile(p); setMessage({ type: 'warning', text: copy.messageNeedsDns }); }} className="text-xs bg-yellow-500 text-white px-3 py-1.5 rounded hover:bg-yellow-600 font-bold shadow-sm">
                                                    {copy.needsDns}
                                                </button>
                                            ) : isSesVerified(p.ses_verification_status) ? (
                                                <button type="button" onClick={() => { setEditingProfile(p); setMessage({ type: 'success', text: copy.messageVerifiedDns }); }} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 font-bold shadow-sm">
                                                    {copy.viewDns}
                                                </button>
                                            ) : !isSesVerified(p.ses_verification_status) && (
                                                <button type="button" onClick={() => setEditingProfile(p)} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 font-bold shadow-sm">
                                                    {copy.startVerifyShort}
                                                </button>
                                            )}

                                            <button onClick={() => setEditingProfile(p)} className="p-2 text-gray-600 hover:bg-gray-100 rounded border border-gray-200" title={copy.edit}><EditIcon /></button>
                                            <button onClick={() => handleDelete(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded border border-gray-200" title={copy.delete}><TrashIcon /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// 4. 重要ページスコアリング設定 (新規追加)
function ScoringSettings({ clientId, isEnglishBeta = false }) {
    const [rules, setRules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const copy = isEnglishBeta ? {
        title: "Lead Scoring",
        body: "Optional: add score points when a tracked lead visits key pages such as pricing, demo, contact, or thank-you pages. This helps prioritize warmer leads in lead management.",
        add: "Add Rule",
        condition: "Condition (URL)",
        matchType: "Match Type",
        score: "Score",
        description: "Description",
        action: "Action",
        loading: "Loading...",
        empty: "No scoring rules yet.",
        exact: "Exact",
        contains: "Contains",
        points: (score) => `+${score} pts`,
        deleteConfirm: "Delete this scoring rule?",
        networkError: "Network error",
    } : {
        title: "重要ページスコアリング設定",
        body: "特定のページ（料金表や問い合わせ完了ページなど）を閲覧したリードに対して、スコアを加算します。\nスコアが高いリードは「温度感が高い」と判断でき、営業優先順位をつけるのに役立ちます。",
        add: "ルール追加",
        condition: "条件 (URL)",
        matchType: "マッチタイプ",
        score: "加算スコア",
        description: "説明",
        action: "操作",
        loading: "読み込み中...",
        empty: "ルールが設定されていません。",
        exact: "完全一致",
        contains: "部分一致",
        points: (score) => `+${score}点`,
        deleteConfirm: "このルールを削除しますか？",
        networkError: "通信エラー",
    };

    const fetchRules = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/scoring-rules?client_id=${clientId}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setRules(data.rules);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchRules(); }, [fetchRules]);

    const handleSave = async (ruleData) => {
        try {
            const method = ruleData.id ? "PUT" : "POST";
            const url = ruleData.id 
                ? `${API_URL}/api/marketing/scoring-rules/${ruleData.id}` 
                : `${API_URL}/api/marketing/scoring-rules`;
            
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ ...ruleData, client_id: clientId })
            });
            const data = await res.json();
            if (data.success) {
                setShowModal(false);
                setEditingRule(null);
                fetchRules();
            } else {
                alert(data.error);
            }
        } catch (e) { alert(e.message); }
    };

    const handleDelete = async (id) => {
        if(!window.confirm(copy.deleteConfirm)) return;
        try {
            const res = await fetch(`${API_URL}/api/marketing/scoring-rules/${id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            if (res.ok) fetchRules();
        } catch (e) { alert(copy.networkError); }
    };

    return (
        <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded border border-yellow-200 text-sm text-yellow-800 mb-4">
                <p><strong><StarIcon/> {copy.title}</strong></p>
                <p className="text-xs mt-1 whitespace-pre-line">{copy.body}</p>
            </div>

            <div className="flex justify-stretch sm:justify-end mb-4">
                <button onClick={() => { setEditingRule(null); setShowModal(true); }} className="flex w-full sm:w-auto items-center justify-center gap-1 bg-indigo-600 text-white font-bold py-2 px-4 rounded hover:bg-indigo-700 text-sm">
                    <PlusIcon /> {copy.add}
                </button>
            </div>

            <div className="hidden md:block bg-white rounded-lg shadow border overflow-hidden">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="bg-gray-100 text-gray-700 uppercase">
                        <tr>
                            <th className="p-3">{copy.condition}</th>
                            <th className="p-3">{copy.matchType}</th>
                            <th className="p-3 text-center">{copy.score}</th>
                            <th className="p-3">{copy.description}</th>
                            <th className="p-3 text-center">{copy.action}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {isLoading ? <tr><td colSpan="5" className="p-8 text-center">{copy.loading}</td></tr> :
                         rules.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-gray-500">{copy.empty}</td></tr> :
                         rules.map(rule => (
                            <tr key={rule.id} className="hover:bg-gray-50">
                                <td className="p-3 font-mono font-bold text-gray-800">{rule.url_pattern}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-1 rounded text-xs ${rule.match_type === 'exact' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                        {rule.match_type === 'exact' ? copy.exact : copy.contains}
                                    </span>
                                </td>
                                <td className="p-3 text-center">
                                    <span className="font-bold text-indigo-600">{copy.points(rule.score)}</span>
                                </td>
                                <td className="p-3 text-gray-500">{rule.description}</td>
                                <td className="p-3 text-center flex justify-center gap-2">
                                    <button onClick={() => { setEditingRule(rule); setShowModal(true); }} className="text-blue-500 hover:bg-blue-50 p-1.5 rounded"><EditIcon /></button>
                                    <button onClick={() => handleDelete(rule.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><TrashIcon /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="md:hidden space-y-3">
                {isLoading ? (
                    <div className="bg-white rounded-lg border p-6 text-center text-sm text-gray-500">{copy.loading}</div>
                ) : rules.length === 0 ? (
                    <div className="bg-white rounded-lg border p-6 text-center text-sm text-gray-500">{copy.empty}</div>
                ) : rules.map(rule => (
                    <div key={rule.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <span className={`px-2 py-1 rounded text-xs ${rule.match_type === 'exact' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                {rule.match_type === 'exact' ? copy.exact : copy.contains}
                            </span>
                            <span className="font-bold text-indigo-600">{copy.points(rule.score)}</span>
                        </div>
                        <div className="font-mono font-bold text-gray-800 break-all">{rule.url_pattern}</div>
                        {rule.description && <div className="text-sm text-gray-500 break-words">{rule.description}</div>}
                        <div className="flex justify-end gap-2 pt-1">
                            <button onClick={() => { setEditingRule(rule); setShowModal(true); }} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><EditIcon /></button>
                            <button onClick={() => handleDelete(rule.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><TrashIcon /></button>
                        </div>
                    </div>
                ))}
            </div>
            {showModal && <ScoringRuleModal onClose={() => setShowModal(false)} onSave={handleSave} rule={editingRule} isEnglishBeta={isEnglishBeta} />}
        </div>
    );
}

// 5. Meta連携設定 (インスタントフォーム連携 & 期限監視版)
function MetaAdsSettings({ clientId, isEnglishBeta = false }) {
    const { appName } = useBranding();
    const { user } = useOutletContext(); // AppLayoutからuser情報を取得
    const [settings, setSettings] = useState({
        fb_pixel_id: '',
        fb_access_token: '',
        fb_test_event_code: '',
        fb_page_id: '',
        fb_page_name: '',
        fb_token_expires_at: null
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const copy = isEnglishBeta ? {
        loading: "Loading...",
        saveSuccess: "Settings saved.",
        saveFailed: "Failed to save settings.",
        title: "Meta Instant Form Integration",
        subtitle: "Lead Ads Integration",
        description: (
            <>
                If you use Meta Lead Ads, connect a Facebook Page to automatically import leads into {appName} in real time.
            </>
        ),
        connectedPage: "Connected Page",
        tokenExpiry: (days) => `Token expires in: ${days} days`,
        reconnect: "Update connection / change page",
        connectPage: "Connect Facebook Page",
        capiTitle: "CAPI Advanced Settings",
        pixelId: "Meta Pixel ID",
        pixelPlaceholder: "Example: 742003200000000",
        testCode: "Test Event Code",
        testPlaceholder: "Example: TEST12345",
        accessToken: "CAPI Access Token",
        saving: "Saving...",
        save: "Save Settings"
    } : {
        loading: "読み込み中...",
        saveSuccess: "設定を保存しました。",
        saveFailed: "保存に失敗しました。",
        title: "Meta インスタントフォーム連携",
        subtitle: "Lead Ads Integration",
        description: (
            <>
                Metaの「リード獲得広告」を使用している場合、Facebookページと連携することで、<br/>
                <strong>フォームに届いたリードを{appName}へリアルタイムに自動取り込み</strong>できます。
            </>
        ),
        connectedPage: "連携中のページ",
        tokenExpiry: (days) => `トークン有効期限: ${days}日`,
        reconnect: "連携を更新・ページを変更する",
        connectPage: "Facebookページと連携する",
        capiTitle: "CAPI 詳細設定 (高度な設定)",
        pixelId: "Meta ピクセルID",
        pixelPlaceholder: "例: 742003200000000",
        testCode: "テストイベントコード",
        testPlaceholder: "例: TEST12345",
        accessToken: "CAPI アクセストークン",
        saving: "保存中...",
        save: "設定を保存する"
    };

    // ★ 修正：管理機能（期限更新ボタン等）の表示用としてのみ使用し、機能制限には使わない
    const isAdmin = user?.role === 'admin' || user?.role === 'metatest';

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await fetch(`${API_URL}/api/client-info?client_id=${clientId}`, { headers: getAuthHeader() });
                const data = await res.json();
                if (data.success && data.info) {
                    setSettings({
                        fb_pixel_id: data.info.fb_pixel_id || '',
                        fb_access_token: data.info.fb_access_token || '',
                        fb_test_event_code: data.info.fb_test_event_code || '',
                        fb_page_id: data.info.fb_page_id || '',
                        fb_page_name: data.info.fb_page_name || '',
                        fb_token_expires_at: data.info.fb_token_expires_at || null
                    });
                }
            } catch (e) { console.error(e); }
            finally { setIsLoading(false); }
        };
        fetchSettings();
    }, [clientId]);

    const getDaysRemaining = (expiryDate) => {
        if (!expiryDate) return null;
        const diff = new Date(expiryDate) - new Date();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };

    const daysRemaining = getDaysRemaining(settings.fb_token_expires_at);

    const handleFacebookLogin = () => {
        // auth.jsの関数を使って確実にトークンを取得
        const token = getToken(); 
        
        // URLに正しくトークンを付与
        window.location.href = `${API_URL}/api/auth/facebook?client_id=${clientId}&token=${token}`;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/client-info`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ ...settings, client_id: clientId })
            });
            if (res.ok) alert(copy.saveSuccess);
            else alert(copy.saveFailed);
        } catch (e) { alert(e.message); }
        finally { setIsSaving(false); }
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500">{copy.loading}</div>;

    return (
        <div className="space-y-6">
            {/* --- セクションA: インスタントフォーム連携 (OAuth) --- */}
            <div className="bg-gradient-to-br from-indigo-50 to-white p-5 sm:p-6 rounded-2xl shadow-sm border border-indigo-100">
                <div className="flex items-start sm:items-center justify-between mb-6">
                    <div className="flex items-start sm:items-center gap-3">
                        <div className="bg-indigo-600 p-2.5 rounded-xl text-white shadow-lg shadow-indigo-100">
                            <i className="fab fa-facebook-f text-xl"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-gray-800">{copy.title}</h3>
                            <p className="text-[10px] text-indigo-500 font-bold uppercase tracking-widest">{copy.subtitle}</p>
                        </div>
                    </div>
                    {/* ★ 修正：審査中バッジを削除 */}
                </div>

                <div className="p-5 bg-white rounded-2xl border border-indigo-50 mb-6 relative overflow-hidden">
                    {/* ★ 修正：Coming Soon オーバーレイを完全に削除 */}

                    <p className="text-xs text-gray-600 leading-relaxed mb-4">
                        {copy.description}
                    </p>

                    {settings.fb_page_id ? (
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-green-50 rounded-xl border border-green-100">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="bg-green-500 w-2 h-2 rounded-full animate-pulse"></div>
                                <div className="min-w-0">
                                    <div className="text-xs font-black text-green-800 uppercase tracking-tighter">{copy.connectedPage}</div>
                                    <div className="text-sm font-bold text-green-900 break-words">{settings.fb_page_name}</div>
                                </div>
                            </div>
                            <div className="text-left md:text-right">
                                <div className={`text-[10px] font-black uppercase ${daysRemaining < 10 ? 'text-red-500' : 'text-green-600'}`}>
                                    {copy.tokenExpiry(daysRemaining)}
                                </div>
                                {/* ★ 修正：クライアント自身でも再連携（トークン更新）ができるように解放 */}
                                <button onClick={handleFacebookLogin} className="text-[10px] font-bold text-indigo-600 hover:underline mt-1">
                                    {copy.reconnect}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button 
                            onClick={handleFacebookLogin}
                            disabled={false} // ★ 修正：disabled制限を解除
                            className="w-full md:w-auto text-white font-black py-4 px-10 rounded-xl shadow-xl transition-all flex items-center justify-center gap-3 bg-[#1877F2] shadow-blue-100 hover:bg-[#166fe5] active:scale-95"
                        >
                            <i className="fab fa-facebook"></i>
                            {copy.connectPage}
                        </button>
                    )}
                </div>
                {/* ★ 修正：管理者用テストアナウンスを削除 */}
            </div>

            {/* --- セクションB: CAPI詳細設定 (マニュアル) --- */}
            <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-start sm:items-center gap-2 mb-6">
                    <div className="bg-gray-800 p-2 rounded-lg text-white"><i className="fas fa-terminal"></i></div>
                    <h3 className="text-lg font-bold text-gray-800">{copy.capiTitle}</h3>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">{copy.pixelId}</label>
                            <input 
                                type="text" 
                                className="w-full p-4 border border-gray-100 rounded-xl font-mono text-sm bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all" 
                                placeholder={copy.pixelPlaceholder}
                                value={settings.fb_pixel_id}
                                onChange={e => setSettings({...settings, fb_pixel_id: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">{copy.testCode}</label>
                            <input 
                                type="text" 
                                className="w-full p-4 border border-gray-100 rounded-xl font-mono text-sm bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all" 
                                placeholder={copy.testPlaceholder}
                                value={settings.fb_test_event_code}
                                onChange={e => setSettings({...settings, fb_test_event_code: e.target.value})}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">{copy.accessToken}</label>
                        <textarea 
                            className="w-full p-4 border border-gray-100 rounded-xl font-mono text-xs h-32 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all leading-relaxed" 
                            placeholder="EAAG..."
                            value={settings.fb_access_token}
                            onChange={e => setSettings({...settings, fb_access_token: e.target.value})}
                        ></textarea>
                    </div>

                    <div className="pt-6 border-t flex justify-stretch sm:justify-end">
                        <button 
                            type="submit" 
                            disabled={isSaving}
                            className="w-full sm:w-auto bg-gray-900 text-white font-black py-4 px-12 rounded-full hover:bg-gray-800 disabled:bg-gray-200 shadow-2xl transition-all active:scale-95"
                        >
                            {isSaving ? copy.saving : copy.save}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- 6. 決済連携設定 (Stripe Connect) ---
function PaymentSettings({ clientId, isEnglishBeta = false }) {
    const { appName } = useBranding();
    const [settings, setSettings] = useState({
        stripe_account_id: '',
        stripe_onboarding_complete: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const copy = isEnglishBeta ? {
        loading: "Loading...",
        verifyComplete: "Stripe connection completed. Card payment is now enabled.",
        verifyPending: "Stripe is reviewing the account or onboarding is incomplete. This may take a few days.",
        verifyError: "Network error while checking the latest status.",
        onboardFailed: "Failed to create the connection URL: ",
        unknownError: "Unknown error",
        title: "Payment Integration (Stripe)",
        subtitle: "Accept card payments on CRM invoices.",
        body: (
            <>
                Connect Stripe to add a payment link to invoices issued from {appName}.<br/>
                When a customer pays by card, the invoice can be marked as paid automatically and the payment can be sent as a Purchase conversion signal.<br/>
                Stripe processing fees apply and Stripe may request business verification during onboarding.
            </>
        ),
        enabled: "Payment: Enabled (Connected)",
        pending: "Review / setup in progress",
        checkLatest: "Check Latest Status",
        resume: "Resume onboarding",
        connect: "Connect Stripe to Enable Payments",
        preparing: "Preparing...",
        disclosureTitle: "Business disclosure page",
        disclosureBody: "Stripe may require business disclosure information during review. This page is generated from the sender profile information.",
        disclosureLink: "Preview generated business disclosure page"
    } : {
        loading: "読み込み中...",
        verifyComplete: "✅ Stripe連携が正常に完了しました！決済機能が有効です。",
        verifyPending: "⏳ Stripe側で審査中、または入力が未完了です。完了まで数日かかる場合があります。",
        verifyError: "通信エラーが発生しました。",
        onboardFailed: "連携URLの取得に失敗しました: ",
        unknownError: "不明なエラー",
        title: "オンライン決済 (Stripe) 連携",
        subtitle: "請求書にクレジットカード払い機能を追加します",
        body: (
            <>
                Stripeと連携すると、{appName}で発行する請求書に<strong>決済用リンク</strong>が自動付与されます。<br/>
                顧客がカードで支払うと、入金が自動消込され、Meta広告の最適化イベント（Purchase）として即座に送信されます。<br/>
                Stripeの決済手数料3.6%が発生します。
            </>
        ),
        enabled: "✅ 決済機能：有効 (連携済み)",
        pending: "⏳ 審査・設定中（Stripe確認待ち）",
        checkLatest: "最新の状況を確認する",
        resume: "入力を再開する",
        connect: "Stripeと連携して決済を有効にする",
        preparing: "準備中...",
        disclosureTitle: "審査用：特商法プレビュー",
        disclosureBody: "決済審査に必要な「特定商取引法に基づく表記」は、送信元設定の情報を元に自動生成されています。",
        disclosureLink: "自動生成された特商法ページを確認する"
    };
    const { verifyComplete, verifyPending, verifyError } = copy;

    const handleVerifyStatus = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/stripe/connect/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() }
            });
            const data = await res.json();
            
            if (data.success) {
                if (data.completed) {
                    // status: 1 の場合
                    setSettings(prev => ({ ...prev, stripe_onboarding_complete: 1 }));
                    alert(verifyComplete);
                } else {
                    // status: 2 の場合 (ここが今の「無反応」の原因)
                    alert(verifyPending);
                }
            }
        } catch (e) { 
            console.error("ステータス更新失敗:", e); 
            alert(verifyError);
        }
    }, [verifyComplete, verifyError, verifyPending]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await fetch(`${API_URL}/api/client-info?client_id=${clientId}`, { headers: getAuthHeader() });
                const data = await res.json();
                if (data.success && data.info) {
                    setSettings({
                        stripe_account_id: data.info.stripe_account_id || '',
                        stripe_onboarding_complete: data.info.stripe_onboarding_complete || 0
                    });
                    const urlParams = new URLSearchParams(window.location.search);
                    if (urlParams.get('status') === 'success' && !data.info.stripe_onboarding_complete) {
                        handleVerifyStatus();
                    }
                }
            } catch (e) { console.error(e); }
            finally { setIsLoading(false); }
        };
        fetchSettings();
    }, [clientId, handleVerifyStatus]);

    const handleConnect = async () => {
        setIsConnecting(true);
        try {
            const res = await fetch(`${API_URL}/api/stripe/connect/onboard`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            const data = await res.json();
            if (data.success && data.url) {
                window.location.href = data.url; // Stripeのオンボーディング画面へリダイレクト
            } else {
                alert(copy.onboardFailed + (data.error || copy.unknownError));
            }
        } catch (e) { alert(e.message); }
        finally { setIsConnecting(false); }
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500">{copy.loading}</div>;

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-3xl shadow-sm border border-indigo-100">
                <div className="flex items-start sm:items-center gap-3 mb-6">
                    <div className="bg-indigo-600 p-3 rounded-2xl text-white shadow-lg shadow-indigo-200">
                        <CreditCardIcon />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-800">{copy.title}</h3>
                        <p className="text-xs text-gray-400 font-medium">{copy.subtitle}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 leading-relaxed">
                            {copy.body}
                        </p>
                        
                        {settings.stripe_onboarding_complete === 1 ? (
                            <div className="bg-green-100 text-green-700 px-4 py-2 rounded-xl font-bold border border-green-200">
                                {copy.enabled}
                            </div>
                        ) : settings.stripe_onboarding_complete === 2 ? (
                            <div className="space-y-3">
                                <div className="bg-amber-100 text-amber-700 px-4 py-2 rounded-xl font-bold border border-amber-200 inline-block">
                                    {copy.pending}
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <button 
                                        onClick={handleVerifyStatus}
                                        className="w-full sm:w-auto text-xs bg-white border border-gray-300 px-4 py-2 rounded-lg font-bold hover:bg-gray-50"
                                    >
                                        {copy.checkLatest}
                                    </button>
                                    <button 
                                        onClick={handleConnect}
                                        className="text-xs text-indigo-600 font-bold hover:underline"
                                    >
                                        {copy.resume}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button 
                                onClick={handleConnect}
                                disabled={isConnecting}
                                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 px-8 rounded-xl shadow-xl transition-all active:scale-95 disabled:bg-gray-400"
                            >
                                {isConnecting ? copy.preparing : copy.connect}
                            </button>
                        )}
                    </div>
                    
                    <div className="p-5 sm:p-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">{copy.disclosureTitle}</h4>
                        <p className="text-[11px] text-gray-500 mb-4">{copy.disclosureBody}</p>
                        <a 
                            href={`${window.location.origin}/legal/${clientId}`} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-xs font-bold text-indigo-600 hover:underline flex items-center gap-1"
                        >
                            {copy.disclosureLink} <ArrowRightIcon />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScoringRuleModal({ onClose, onSave, rule, isEnglishBeta = false }) {
    const [formData, setFormData] = useState({
        url_pattern: '',
        match_type: 'contains',
        score: 10,
        description: ''
    });
    const copy = isEnglishBeta ? {
        titleEdit: "Edit Rule",
        titleNew: "New Scoring Rule",
        targetUrl: "Target Page URL or Path",
        targetPlaceholder: "Example: https://example.com/thanks",
        targetHelp: "Paste the page URL or path you want to score.",
        matchType: "Match Type",
        contains: "Contains",
        exact: "Exact",
        score: "Score",
        description: "Description (optional)",
        descriptionPlaceholder: "Example: Pricing page visit",
        cancel: "Cancel",
        save: "Save"
    } : {
        titleEdit: "ルール編集",
        titleNew: "新規スコアリングルール",
        targetUrl: "対象ページのURL (またはパス)",
        targetPlaceholder: "例: https://example.com/thanks",
        targetHelp: "ブラウザのURLをそのままコピペして貼り付けてください。",
        matchType: "マッチタイプ",
        contains: "部分一致 (含む)",
        exact: "完全一致",
        score: "加算スコア",
        description: "説明 (任意)",
        descriptionPlaceholder: "例: 料金ページ閲覧",
        cancel: "キャンセル",
        save: "保存"
    };

    useEffect(() => {
        if (rule) {
            setFormData(rule);
        }
    }, [rule]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="font-bold text-lg mb-4">{rule ? copy.titleEdit : copy.titleNew}</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold mb-1">{copy.targetUrl}</label>
                        <input 
                            type="text" 
                            name="url_pattern" 
                            required 
                            className="w-full border p-2 rounded font-mono text-sm" 
                            placeholder={copy.targetPlaceholder} 
                            value={formData.url_pattern} 
                            onChange={handleChange} 
                        />
                        <p className="text-xs text-gray-500 mt-1">{copy.targetHelp}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold mb-1">{copy.matchType}</label>
                            <select 
                                name="match_type" 
                                className="w-full border p-2 rounded bg-white text-sm" 
                                value={formData.match_type} 
                                onChange={handleChange}
                            >
                                <option value="contains">{copy.contains}</option>
                                <option value="exact">{copy.exact}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold mb-1">{copy.score}</label>
                            <input 
                                type="number" 
                                name="score" 
                                required 
                                min="1" 
                                className="w-full border p-2 rounded" 
                                value={formData.score} 
                                onChange={handleChange} 
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold mb-1">{copy.description}</label>
                        <input 
                            type="text" 
                            name="description" 
                            className="w-full border p-2 rounded" 
                            placeholder={copy.descriptionPlaceholder} 
                            value={formData.description} 
                            onChange={handleChange} 
                        />
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
                        <button type="button" onClick={onClose} className="w-full sm:w-auto px-4 py-2 border rounded hover:bg-gray-50">{copy.cancel}</button>
                        <button type="submit" className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white font-bold rounded hover:bg-indigo-700">{copy.save}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- Main Page Component ---

export default function MarketingTrackingPage({ defaultTab = 'sender' }) {
    const { user } = useOutletContext(); // userを取得
    const [searchParams] = useSearchParams();
    const isEnglishBeta = isEnglishOperatorMode();

    // 🚀 修正: OEM社長(admin)がプルダウンで選択したクライアントを反映
    const clientId = useMemo(() => (
      user?.role === "admin" || user?.role === "admin_staff" 
        ? localStorage.getItem("selectedClientId") || "" 
        : user?.client_id
    ), [user]);

    const requestedTab = searchParams.get('tab');
    const validTabs = new Set(['sender', 'ng_list', 'tracking', 'meta_ads', 'payment', 'scoring']);
    const englishAllowedTabs = new Set(['sender', 'tracking', 'meta_ads', 'payment', 'scoring']);
    const normalizedDefaultTab = validTabs.has(defaultTab) ? defaultTab : 'sender';
    const normalizedRequestedTab = requestedTab === 'domains' ? 'sender' : (requestedTab || normalizedDefaultTab);
    const initialTab = !validTabs.has(normalizedRequestedTab) || (isEnglishBeta && !englishAllowedTabs.has(normalizedRequestedTab))
        ? 'sender'
        : normalizedRequestedTab;
    const [activeTab, setActiveTab] = useState(initialTab); 
    
    const [isFreePlan, setIsFreePlan] = useState(false);

    useEffect(() => {
        fetch(`${API_URL}/api/usage-status?clientId=${clientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => { if (data.success) setIsFreePlan(data.plan === 'free'); });
    }, [clientId]);

    const tabs = isEnglishBeta ? [
        { id: 'sender', label: 'Sender & DNS' },
        { id: 'tracking', label: 'Tracking Tag' },
        { id: 'meta_ads', label: 'Meta Integration' },
        { id: 'payment', label: 'Payment Integration' },
        { id: 'scoring', label: 'Lead Scoring' },
    ] : [
        { id: 'sender', label: '送信元設定' },
        { id: 'ng_list', label: '配信停止リスト' },
        { id: 'tracking', label: 'トラッキングタグ' },
        { id: 'meta_ads', label: 'Meta連携設定' },
        { id: 'payment', label: '決済連携' },
        { id: 'scoring', label: 'スコアリング' },
    ];

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-6xl mx-auto">
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-tight">
                        {isEnglishBeta ? 'Marketing Settings' : 'マーケティング設定'}
                    </h1>
                    <p className="text-sm text-gray-500 font-medium">
                        {isEnglishBeta ? 'Sender domains, tracking tags, and Meta lead integrations.' : 'フォーム、LP、メール、リード計測の管理センター'}
                    </p>
                </div>

                <div className="-mx-3 sm:mx-0 px-3 sm:px-0 flex border-b border-gray-200 mb-6 sm:mb-8 overflow-x-auto gap-2">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`shrink-0 py-3 px-4 sm:px-6 font-black text-sm border-b-2 transition-all whitespace-nowrap ${
                                activeTab === tab.id 
                                ? 'border-blue-600 text-blue-600' 
                                : 'border-transparent text-gray-400 hover:text-gray-600'
                            }`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="min-h-[400px]">
                    {activeTab === 'domains' && <DomainSettingsOverview clientId={clientId} />}
                    {activeTab === 'meta_ads' && (isFreePlan ? <div className="bg-white p-8 sm:p-20 text-center rounded-2xl border-2 border-dashed font-bold">{isEnglishBeta ? 'Available on paid plans only' : '有料プラン限定'}</div> : <MetaAdsSettings clientId={clientId} isEnglishBeta={isEnglishBeta} />)}
                    {activeTab === 'sender' && <SenderSettings clientId={clientId} isEnglishBeta={isEnglishBeta} />}
                    {activeTab === 'ng_list' && <NgListSettings clientId={clientId} />}
                    {activeTab === 'tracking' && <TrackingSettings clientId={clientId} isEnglishBeta={isEnglishBeta} />}
                    {activeTab === 'scoring' && <ScoringSettings clientId={clientId} isEnglishBeta={isEnglishBeta} />}
                    {activeTab === 'payment' && <PaymentSettings clientId={clientId} isEnglishBeta={isEnglishBeta} />}
                </div>
            </div>
        </div>
    );
}
